import * as React from 'react'
import PropTypes from 'prop-types'
import { mergeRefs, useUniqueId } from '@ds/react-utils'
import { consoleWarn, warnInvalidThemeProp } from '../../logging'
import { CustomPropTypes } from '../../support'
import { useIsInk, useThemeStyles } from '../../theming'
import type {
  AutoCapitalize,
  AutoComplete,
  InputForwardRef,
  DOMRef,
} from '../../types'
import { AboveInputContainer } from '../../internal/components/AboveInputContainer'
import { BelowInputContainer } from '../../internal/components/BelowInputContainer'
import { InputCount } from '../../internal/components/InputCount'
import { InputDescription } from '../InputDescription'
import { InputLabel } from '../../internal/components/InputLabel'
import type { InputHelpProps } from '../InputHelp'
import { InputTextBox } from '../InputTextBox'
import styles from './styles'

const fileInputValues = ['accept', 'capture', 'multiple'] as const

const inputModes = [
  'decimal',
  'email',
  'none',
  'numeric',
  'search',
  'tel',
  'text',
  'url',
] as const

const spellCheckValues = ['true', 'false'] as const

export const textBoxTypes = [
  'date',
  'datetime-local',
  'email',
  'month',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'time',
  'url',
  'week',
] as const

export type FileInputProps = (typeof fileInputValues)[number]
export type TextBoxInputMode = (typeof inputModes)[number]
export type TextBoxSpellCheck = (typeof spellCheckValues)[number]
export type TextBoxTextType = (typeof textBoxTypes)[number]

export interface TextBoxProps
  extends Omit<
    React.ComponentPropsWithoutRef<'input'>,
    'children' | 'width' | 'type' | FileInputProps
  > {
  /**
   * Optional accessibility string that overrides the label string.
   */
  accessibilityText?: string
  /**
   * If a (non-zero) value is provided for the 'maxLength' prop, display a "Characters remaining" description using the value of that prop.
   */
  characterCount?: boolean
  /**
   * A React ref to receive the container element, which wraps the input and start/end elements. Can be used to anchor other elements, such as a Menu or Tooltip.
   */
  containerRef?: DOMRef<HTMLDivElement>
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Display the provided node as the "description" of the TextBox.
   */
  description?: string
  /**
   * The element to display at the end of the TextBox.
   */
  endElement?: React.ReactNode
  /**
   * Display the provided text as the "error description" of the TextBox.
   */
  error?: string
  /**
   * A React ref to assign to the HTML node representing the DotBadge element.
   */
  forwardedRef?: InputForwardRef
  /**
   * Visually hide the error message.
   * This is not a publicly encouraged pattern but allows NDSE to use TextBox by visually hiding the error message but keep styling treatment as well keep the icon for accessibility purposes.
   * Only available in Olive theme.
   */
  hideError?: boolean
  /**
   * Hide the TextBox label while allowing assistive devices to identify the control.
   */
  hideLabel?: boolean
  /**
   * The element to add a help tooltip to the right of the Textbox component. Takes an InputHelp component.
   */
  inputHelp?: React.ReactElement<InputHelpProps>
  /**
   * If true the text box will be rendered as invalid (both visually and to screen readers), but no error message will be shown.
   * In most cases you should use the `error` prop instead. When using this prop you should also pass a value for `aria-describedby`.
   */
  invalid?: boolean
  /**
   * The text label for the TextBox.
   */
  label: string
  /**
   * A unique identifier for the label of TextBox
   */
  labelId?: string
  /**
   * The element to display on the left of the TextBox.
   * @deprecated Use `startElement` instead.
   */
  leftElement?: React.ReactNode
  /**
   * The element to display on the right of the TextBox.
   * @deprecated Use `endElement` instead.
   */
  rightElement?: React.ReactNode
  /**
   * The element to display at the start of the TextBox.
   */
  startElement?: React.ReactNode
  /**
   * The width of the TextBox's input element.
   * Note: This only controls the width of the input itself, not the wrapping container.
   * For example, if using a rightElement a custom wrapping div would need a width applied for it to work visually.
   */
  width?: string
  /**
   * <input /> attributes **
   */
  /**
   * Adds the 'autocapitalize' HTML attribute to the rendered `<input>`.
   * More information [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autocapitalize).
   */
  autoCapitalize?: AutoCapitalize
  /**
   * Adds the 'autocomplete' HTML attribute to the rendered `<input>`.
   * More information [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete).
   */
  autoComplete?: AutoComplete
  /**
   * Disable the TextBox.
   */
  disabled?: boolean
  /**
   * A unique identifier for TextBox
   */
  id?: string
  /**
   * When the 'characterCount' prop is 'true', the (non-zero) value of this prop is used when displaying the "Characters remaining" description. When the 'characterCount' prop is 'false', the value of this prop is used for the maxlength HTML attribute of the rendered input element.
   */
  maxLength?: number
  /**
   * The name HTML attribute for the TextArea.
   */
  name?: string
  /**
   * Placeholder text for TextBox.
   */
  placeholder?: string
  /**
   * Adds a visual treatment indicating that the associated control requires a value.
   */
  required?: boolean
  /**
   * Set the TextBox to read-only.
   */
  readOnly?: boolean
  /**
   * The (optional) value for the 'type' attribute of the rendered input element. (e.g. "email", "password", etc.).
   */
  type?: TextBoxTextType
  /**
   * The value of the input.
   */
  value?: string
}

/**
 * Text fields allow users to enter and edit text in the UI. They are often laid out within forms, but can also be used as standalone inputs for data.
 */
export function TextBox({
  accessibilityText,
  characterCount = false,
  containerRef,
  description,
  disabled = false,
  endElement,
  error,
  forwardedRef,
  hideError = false,
  hideLabel = false,
  id,
  inputHelp,
  invalid = false,
  label,
  labelId,
  leftElement,
  startElement,
  maxLength,
  placeholder,
  readOnly = false,
  required = false,
  rightElement,
  type = 'text',
  value,
  width,
  'data-qa': dataQa,
  'aria-describedby': ariaDescribedBy,
  ...restProps
}: TextBoxProps) {
  const sx = useThemeStyles(styles)

  const generatedTextBoxId = useUniqueId('short')
  const textBoxId = id || generatedTextBoxId
  const descriptionId = useUniqueId('short')
  const errorId = useUniqueId('short')
  const isInk = useIsInk()
  const shouldHideError = !isInk && hideError
  const inputRef = React.useRef<HTMLInputElement>(null)
  const mergedRefs = React.useMemo(
    () => mergeRefs(forwardedRef, inputRef),
    [forwardedRef, inputRef],
  )

  if (isInk && hideError) {
    warnInvalidThemeProp('TextBox', 'hideError', 'Ink')
  }

  if (leftElement || rightElement) {
    consoleWarn(
      `The "leftElement" and "rightElement" props have been deprecated in favor of "startElement" and "endElement" respectively.`,
    )
  }

  const textBoxPlaceholder =
    placeholder && required && hideLabel ? `${placeholder} *` : placeholder

  const textBoxStartElementStyles = [
    sx.default.textBoxStartElement,
    disabled && sx.disabled.icon,
  ]

  const textBoxEndElementStyles = [
    sx.default.textBoxEndElement,
    disabled && sx.disabled.icon,
  ]

  const textBoxStartElement = (startElement || leftElement) && (
    <div css={textBoxStartElementStyles} data-qa={dataQa && `${dataQa}-start`}>
      {startElement || leftElement}
    </div>
  )

  const textBoxEndElement = (endElement || rightElement) && (
    <div css={textBoxEndElementStyles} data-qa={dataQa && `${dataQa}-end`}>
      {endElement || rightElement}
    </div>
  )

  const inputHelpNode = inputHelp && (
    <div css={sx.default.help}>{inputHelp}</div>
  )

  const InputLabelNode = (
    <InputLabel
      accessibilityText={accessibilityText}
      disabled={disabled}
      forId={textBoxId}
      hidden={hideLabel}
      id={labelId}
      required={required}
      data-qa={dataQa && `${dataQa}-label`}
    >
      {label}
    </InputLabel>
  )

  const currentLength = value ? value.length : 0

  const inputCountNode = characterCount && maxLength && (
    <InputCount
      inputRef={inputRef}
      id={descriptionId}
      currentLength={currentLength}
      maxLength={maxLength}
      data-qa={dataQa && `${dataQa}-count`}
    />
  )

  const descriptionNode = description && (
    <InputDescription
      disabled={disabled}
      id={descriptionId}
      kind="helper"
      data-qa={dataQa && `${dataQa}-description`}
    >
      {description}
    </InputDescription>
  )

  const errorNode = error && (
    <InputDescription
      id={errorId}
      kind="error"
      data-qa={dataQa && `${dataQa}-error`}
    >
      {shouldHideError ? (
        <span css={sx.hideErrorText} data-qa={dataQa && `${dataQa}-error-text`}>
          {error}
        </span>
      ) : (
        error
      )}
    </InputDescription>
  )

  // These are to help differentiate the placement of the error
  // WithMessage shows up below the input with the error message visible
  // WithoutMessage shows up end of the input with only the icon visible
  const errorNodeWithMessage = !shouldHideError && errorNode
  const errorNodeWithoutMessage = shouldHideError && (
    <span css={sx.hideError}>{errorNode}</span>
  )

  const errorIdValue = error ? errorId : ''
  // eslint-disable-next-line no-nested-ternary
  const descriptionIdValue = useIsInk()
    ? description
      ? descriptionId
      : ''
    : description && !error
      ? descriptionId
      : ''
  const ariaDescribedByValue =
    `${errorIdValue} ${descriptionIdValue} ${ariaDescribedBy || ''}`.trim()

  return (
    <>
      <AboveInputContainer
        description={isInk && descriptionNode ? descriptionNode : undefined}
        hideLabel={hideLabel}
        label={InputLabelNode}
      />
      <div
        css={sx.default.innerWrap}
        style={{ width }}
        data-qa={dataQa && `${dataQa}-inner-wrapper`}
      >
        <InputTextBox
          {...restProps}
          data-qa={dataQa}
          ariaDescribedById={ariaDescribedByValue || undefined}
          containerRef={containerRef}
          disabled={disabled}
          endElement={textBoxEndElement}
          forwardedRef={mergedRefs}
          id={textBoxId}
          invalid={
            !!error ||
            invalid ||
            (maxLength ? currentLength > maxLength : false)
          }
          startElement={textBoxStartElement}
          placeholder={textBoxPlaceholder}
          maxLength={characterCount ? undefined : maxLength}
          readOnly={readOnly}
          required={required}
          type={type}
          value={value}
        />
        {errorNodeWithoutMessage}
        {inputHelpNode}
      </div>

      <BelowInputContainer
        count={inputCountNode || undefined}
        description={!isInk && descriptionNode ? descriptionNode : undefined}
        error={errorNodeWithMessage || undefined}
      />
    </>
  )
}

TextBox.inputModes = inputModes
TextBox.spellCheckValues = spellCheckValues
TextBox.types = textBoxTypes

TextBox.propTypes = {
  accessibilityText: PropTypes.string,
  autoCapitalize: PropTypes.string,
  autoComplete: PropTypes.string,
  characterCount: PropTypes.bool,
  containerRef: CustomPropTypes.ReactRef,
  'data-.*': PropTypes.string,
  'data-qa': PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  endElement: PropTypes.element,
  error: PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  hideError: PropTypes.bool,
  hideLabel: PropTypes.bool,
  id: PropTypes.string,
  inputHelp: PropTypes.element,
  invalid: PropTypes.bool,
  label: PropTypes.string.isRequired,
  labelId: PropTypes.string,
  leftElement: PropTypes.element,
  maxLength: PropTypes.number,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  rightElement: PropTypes.element,
  startElement: PropTypes.element,
  type: PropTypes.oneOf(textBoxTypes),
  value: PropTypes.string,
  width: PropTypes.string,
}

TextBox.displayName = 'TextBox'
